/**
 * Servicio para gestión de Personas (Person)
 *
 * Person representa una persona física con sus datos personales.
 * Una Person puede tener múltiples membresías (Member) en diferentes clubes.
 */

import {
  where,
  orderBy,
  Timestamp,
  collection,
  query,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  limit,
  serverTimestamp,
  documentId,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import {
  getById,
  getAll,
  create,
  update,
  remove,
  subscribeToDocument,
  subscribeToCollection,
} from "./base";
import {
  normalize,
  deriveSectionFromBirthDate,
  isImplausibleBirthDate,
} from "@/lib/utils";
import type {
  Person,
  IdDocumentType,
  MemberType,
  BloodType,
  Gender,
} from "@/types";

const COLLECTION = "persons";

/**
 * Normaliza idDocumentType a formato canónico (mayúsculas web).
 * Acepta valores de web ("CC") y Flutter ("cc", "passport").
 */
function normalizeIdDocType(type: string): IdDocumentType {
  const mapping: Record<string, IdDocumentType> = {
    cc: "CC",
    CC: "CC",
    ti: "TI",
    TI: "TI",
    ce: "CE",
    CE: "CE",
    passport: "PASAPORTE",
    pasaporte: "PASAPORTE",
    PASAPORTE: "PASAPORTE",
    rc: "RC",
    RC: "RC",
    ppt: "PPT",
    PPT: "PPT",
    "permiso por proteccion temporal": "PPT",
  };
  return mapping[type] || "CC";
}

/**
 * Retorna todas las variantes posibles de un idDocumentType para queries cross-platform.
 */
function getIdDocTypeVariants(type: string): string[] {
  const variants: Record<IdDocumentType, string[]> = {
    CC: ["CC", "cc"],
    TI: ["TI", "ti"],
    CE: ["CE", "ce"],
    PASAPORTE: ["PASAPORTE", "passport"],
    RC: ["RC", "rc"],
    PPT: ["PPT", "ppt"],
  };
  return variants[normalizeIdDocType(type)] || [type];
}

/**
 * Normaliza memberType cross-platform.
 * Flutter usa "camper"/"leader", Web usa "acampante"/"lider".
 * Canónico web: "acampante" | "lider"
 */
function normalizeMemberType(type: string | undefined): MemberType {
  const mapping: Record<string, MemberType> = {
    camper: "acampante",
    acampante: "acampante",
    leader: "lider",
    lider: "lider",
    líder: "lider",
  };
  return mapping[type?.toLowerCase() || ""] || "acampante";
}

/**
 * Normaliza gender cross-platform.
 * Acepta "male"/"female", "M"/"F", "m"/"f".
 * Canónico: "male" | "female"
 */
function normalizeGender(gender: string | undefined): Gender {
  if (gender === "male" || gender === "female") return gender;
  const g = gender?.toUpperCase();
  if (g === "M") return "male";
  if (g === "F") return "female";
  return "male";
}

/**
 * B5: Calcula edad desde birthDate (Timestamp Firestore o string ISO).
 * Retorna null si no hay birthDate o es inválido.
 */
function calcAge(birthDate: unknown): number | null {
  if (!birthDate) return null;
  let d: Date | null = null;
  // Date PRIMERO: getAll() pasa todo por convertTimestamps(), que convierte
  // los Timestamp de Firestore en Date. Sin esta rama, calcAge devolvía null
  // para cualquier ficha leída por esa vía —o sea casi todas— e
  // isAdultPerson caía en su fallback conservador y las trataba como adultas.
  // Efecto: la regla "un adulto puede compartir correo con sus hijos" nunca
  // funcionó; el sistema bloqueaba como si fuera un correo por persona.
  // Lo destapó Keylor Rendón, 6 años, contado como adulto (2026-09-28).
  if (birthDate instanceof Date) d = birthDate;
  else if (birthDate instanceof Timestamp) d = birthDate.toDate();
  else if (typeof birthDate === "string") d = new Date(birthDate);
  else if (
    typeof birthDate === "object" &&
    birthDate !== null &&
    "toDate" in birthDate &&
    typeof (birthDate as { toDate: unknown }).toDate === "function"
  ) {
    d = (birthDate as { toDate: () => Date }).toDate();
  } else if (
    typeof birthDate === "object" &&
    birthDate !== null &&
    "seconds" in birthDate &&
    typeof (birthDate as { seconds: unknown }).seconds === "number"
  ) {
    // Timestamp serializado ({seconds, nanoseconds}), como llega en algunos
    // snapshots ya pasados por JSON.
    d = new Date((birthDate as { seconds: number }).seconds * 1000);
  }
  if (!d || isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age;
}

/**
 * B5: ¿La persona se considera adulta? (>=18).
 * Sin birthDate: tratada como adulta (conservador) para evitar bypass de validación.
 */
function isAdultPerson(p: { birthDate?: unknown }): boolean {
  const age = calcAge(p.birthDate);
  return age === null ? true : age >= 18;
}

export const personService = {
  // ==================== LECTURA ====================

  async getById(id: string): Promise<Person | null> {
    return getById<Person>(COLLECTION, id);
  },

  async getAll(): Promise<Person[]> {
    return getAll<Person>(COLLECTION, [orderBy("fullName", "asc")]);
  },

  /**
   * Buscar persona por número de documento
   * Útil para verificar si ya existe antes de crear
   */
  async getByDocument(
    idDocumentType: Person["idDocumentType"] | string,
    idDocumentNumber: string,
  ): Promise<Person | null> {
    const typeVariants = getIdDocTypeVariants(idDocumentType);
    const results = await getAll<Person>(COLLECTION, [
      where("idDocumentType", "in", typeVariants),
      where("idDocumentNumber", "==", idDocumentNumber),
    ]);
    return results.length > 0 ? results[0] : null;
  },

  /**
   * Busca TODAS las personas con un número de documento (sin filtrar por tipo).
   * Usado para validar unicidad en el espacio NUIP: en Colombia la TI y la CC
   * comparten el MISMO número por persona, así que dos personas distintas no
   * deberían tener el mismo número aunque sea con tipos distintos.
   */
  async getAllByDocumentNumber(idDocumentNumber: string): Promise<Person[]> {
    if (!idDocumentNumber) return [];
    return getAll<Person>(COLLECTION, [
      where("idDocumentNumber", "==", idDocumentNumber),
    ]);
  },

  /**
   * Busca la ficha EXISTENTE que se debe REUTILIZAR para un documento dado,
   * espejando exactamente la guarda de unicidad (B4/B4b):
   *   - match exacto por tipo+número, y
   *   - si el tipo es CC o TI, también match cross-type por número (en Colombia
   *     TI y CC comparten el mismo número → es la MISMA persona).
   * Incluye fichas inactivas/huérfanas (las que bloquean la unicidad pero no
   * aparecen en la lista de miembros). Si hay varias candidatas, prefiere una
   * activa y, a igualdad, la más recientemente actualizada.
   *
   * Sirve para que los flujos de alta REUTILICEN en vez de chocar contra el
   * bloqueo (causa del "ya existe una persona con el mismo documento" sin salida).
   */
  async findReusableByDocument(
    idDocumentType: Person["idDocumentType"] | string,
    idDocumentNumber: string,
  ): Promise<Person | null> {
    if (!idDocumentNumber) return null;
    const type = normalizeIdDocType(idDocumentType);
    const sameNumber = await personService.getAllByDocumentNumber(
      idDocumentNumber.trim(),
    );
    if (sameNumber.length === 0) return null;

    const isNuip = type === "CC" || type === "TI";
    const candidates = sameNumber.filter((p) =>
      isNuip
        ? p.idDocumentType === "CC" || p.idDocumentType === "TI"
        : normalizeIdDocType(p.idDocumentType) === type,
    );
    if (candidates.length === 0) return null;

    const updatedMs = (p: Person) =>
      (p.updatedAt as { toMillis?: () => number })?.toMillis?.() ?? 0;
    candidates.sort((a, b) => {
      const activeDiff =
        (b.status === "active" ? 1 : 0) - (a.status === "active" ? 1 : 0);
      if (activeDiff !== 0) return activeDiff;
      return updatedMs(b) - updatedMs(a);
    });
    return candidates[0];
  },

  /**
   * F11 — Clasifica el estado de un documento para el setup SIN exponer PII.
   * Intenta primero la CF `checkPersonDocument` (que devuelve solo el estado y
   * un correo enmascarado); si la CF no está disponible (dev con IAM roto,
   * emulador, despliegue a medias) cae al query client-side de siempre y
   * clasifica localmente.
   */
  async checkDocumentStatus(
    idDocumentType: Person["idDocumentType"] | string,
    idDocumentNumber: string,
    currentUid: string,
  ): Promise<{
    status: "free" | "mine" | "unlinked" | "linked_other" | "guardian" | "ambiguous";
    maskedEmail?: string;
    personId?: string;
  }> {
    try {
      const { httpsCallable } = await import("firebase/functions");
      const { functions } = await import("../firebase");
      const fn = httpsCallable<
        { idDocumentType: string; idDocumentNumber: string },
        { status: "free" | "mine" | "unlinked" | "linked_other" | "guardian" | "ambiguous"; maskedEmail?: string; personId?: string }
      >(functions, "checkPersonDocument");
      const res = await fn({
        idDocumentType: String(idDocumentType),
        idDocumentNumber,
      });
      return res.data;
    } catch (e) {
      console.warn("[checkDocumentStatus] CF no disponible, fallback client-side:", e);
      const person = await personService.findReusableByDocument(
        idDocumentType,
        idDocumentNumber,
      );
      if (!person) return { status: "free" };
      // Paridad con la CF: las fichas marcadas duplicadas no bloquean.
      if ((person as Person & { _duplicateOf?: string })._duplicateOf)
        return { status: "free" };
      if (person.linkedUserId === currentUid)
        return { status: "mine", personId: person.id };
      if (person.linkedUserId) {
        const email = person.email;
        const masked =
          email && email.includes("@")
            ? `${email.slice(0, 2)}${"*".repeat(Math.max(3, email.split("@")[0].length - 2))}@${email.split("@")[1]}`
            : undefined;
        return { status: "linked_other", maskedEmail: masked };
      }
      if (person.guardianUserId) return { status: "guardian" };
      return { status: "unlinked" };
    }
  },

  /**
   * Resumen legible de DÓNDE está una persona (para mensajes accionables).
   * Ej: "Ya está inscrita en 1 club.", "No está inscrita en ningún club
   * (ficha existente sin membresía).", "Ficha inactiva.".
   */
  async membershipSummary(personId: string): Promise<string> {
    try {
      const snap = await getDocs(
        query(collection(db, "members"), where("personId", "==", personId)),
      );
      const active = snap.docs.filter(
        (d) => (d.data() as { status?: string }).status !== "inactive",
      );
      if (active.length > 0)
        return `Ya está inscrita en ${active.length} club(es); edítela ahí en lugar de crear una nueva.`;
      return "No está inscrita en ningún club (ficha existente sin membresía); reutilícela en vez de crear otra.";
    } catch {
      return "Reutilice esa ficha existente en vez de crear una nueva.";
    }
  },

  /**
   * Buscar persona por email (retorna la primera)
   */
  async getByEmail(email: string): Promise<Person | null> {
    const results = await getAll<Person>(COLLECTION, [
      where("email", "==", email.toLowerCase()),
    ]);
    return results.length > 0 ? results[0] : null;
  },

  /**
   * Buscar todas las personas por email
   * Usado para vincular múltiples personas pre-registradas al mismo usuario
   */
  async getAllByEmail(email: string): Promise<Person[]> {
    return getAll<Person>(COLLECTION, [
      where("email", "==", email.toLowerCase()),
    ]);
  },

  /**
   * Buscar todas las personas cuyo `_legacyEmail` coincida (post-Fase-7).
   *
   * Cuando una persona migra al guardian model y su email original era el
   * de un acudiente, dicho email se archiva en `_legacyEmail` y el campo
   * `email` queda libre. Para que un acudiente que se registra ahora pueda
   * encontrar a sus pupilos, también buscamos por `_legacyEmail`.
   */
  async getAllByLegacyEmail(email: string): Promise<Person[]> {
    return getAll<Person>(COLLECTION, [
      where("_legacyEmail", "==", email.toLowerCase()),
    ]);
  },

  /**
   * B5: Retorna personas ADULTAS (>=18) registradas con este email.
   * Personas sin birthDate se cuentan como adultas (conservador).
   * Usado para validar unicidad de email entre adultos.
   */
  async getAdultsByEmail(email: string, excludeId?: string): Promise<Person[]> {
    const all = await personService.getAllByEmail(email);
    return all.filter((p) => p.id !== excludeId && isAdultPerson(p));
  },

  /**
   * Buscar persona por linkedUserId (cuenta de usuario)
   */
  async getByLinkedUser(userId: string): Promise<Person | null> {
    const results = await getAll<Person>(COLLECTION, [
      where("linkedUserId", "==", userId),
    ]);
    return results.length > 0 ? results[0] : null;
  },

  /**
   * Buscar persona por UID de auth (compatibilidad)
   * 1) linkedUserId (modelo actual)
   * 2) userId (campo legado en datos históricos)
   */
  async getByAuthUserId(
    userId: string,
    email?: string,
  ): Promise<Person | null> {
    const linked = await personService.getByLinkedUser(userId);
    if (linked) return linked;

    const legacy = await getAll<Person>(COLLECTION, [
      where("userId", "==", userId),
    ]);
    if (legacy.length > 0) return legacy[0];

    if (email) {
      const byEmail = await personService.getByEmail(email);
      if (byEmail) return byEmail;
    }

    return null;
  },

  /**
   * Buscar personas por iglesia
   */
  async getByChurch(churchId: string): Promise<Person[]> {
    return getAll<Person>(COLLECTION, [
      where("churchId", "==", churchId),
      where("status", "==", "active"),
      orderBy("fullName", "asc"),
    ]);
  },

  /**
   * Buscar personas por asociación
   */
  async getByAssociation(associationId: string): Promise<Person[]> {
    return getAll<Person>(COLLECTION, [
      where("associationId", "==", associationId),
      where("status", "==", "active"),
      orderBy("fullName", "asc"),
    ]);
  },

  /**
   * Buscar personas activas (para selectores)
   */
  async getActive(): Promise<Person[]> {
    return getAll<Person>(COLLECTION, [
      where("status", "==", "active"),
      orderBy("fullName", "asc"),
    ]);
  },

  /**
   * Cargar múltiples personas por IDs (en lotes de 30 para respetar límite de Firestore)
   * Más eficiente que N llamadas a getById en paralelo
   */
  async getByIds(ids: string[]): Promise<Person[]> {
    if (ids.length === 0) return [];
    const CHUNK_SIZE = 30; // Límite de Firestore para operador 'in'
    const chunks: string[][] = [];
    for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
      chunks.push(ids.slice(i, i + CHUNK_SIZE));
    }
    const results = await Promise.all(
      chunks.map((chunk) =>
        getAll<Person>(COLLECTION, [where(documentId(), "in", chunk)]),
      ),
    );
    return results.flat();
  },

  // ==================== ESCRITURA ====================

  async create(
    data: Omit<Person, "id" | "createdAt" | "updatedAt">,
    userId: string,
  ): Promise<string> {
    // Normalizar campos de texto
    const normalizedFirstName = normalize.name(data.firstName);
    const normalizedLastName = normalize.name(data.lastName);
    const normalizedDocType = normalizeIdDocType(data.idDocumentType || "CC");
    const normalizedDocNumber = normalize.documentNumber(
      data.idDocumentNumber || "",
    );

    // B4: Validar unicidad del documento de identidad
    // (se omite si el número viene vacío — ej: RC sin registro)
    if (normalizedDocNumber) {
      const duplicate = await personService.getByDocument(
        normalizedDocType,
        normalizedDocNumber,
      );
      if (duplicate) {
        const ubic = await personService.membershipSummary(duplicate.id);
        throw new Error(
          `Ya existe una persona con ${normalizedDocType} ${normalizedDocNumber}: ${duplicate.fullName}. ${ubic}`,
        );
      }
    }

    // B4b: Unicidad en el espacio NUIP — en Colombia la TI y la CC comparten el
    // mismo número por persona, así que dos personas distintas no pueden tener el
    // mismo número aunque sea con tipos distintos (evita cédulas duplicadas TI/CC).
    if (
      normalizedDocNumber &&
      (normalizedDocType === "CC" || normalizedDocType === "TI")
    ) {
      const sameNumber =
        await personService.getAllByDocumentNumber(normalizedDocNumber);
      const conflict = sameNumber.find(
        (p) => p.idDocumentType === "CC" || p.idDocumentType === "TI",
      );
      if (conflict) {
        const ubic = await personService.membershipSummary(conflict.id);
        throw new Error(
          `El número ${normalizedDocNumber} ya pertenece a ${conflict.fullName} (${conflict.idDocumentType}). En Colombia la TI y la CC comparten el mismo número por persona, así que es la misma persona. ${ubic}`,
        );
      }
    }

    // B4c: Fecha de nacimiento plausible (evita años mal digitados, ej. 2026 → edad 0).
    if (isImplausibleBirthDate(data.birthDate)) {
      throw new Error(
        "La fecha de nacimiento no es válida (futura o edad fuera de rango). Revisa el año ingresado.",
      );
    }

    // B5: Validar unicidad de email entre adultos.
    // Política: un email puede repetirse entre 1 adulto + N menores (padre/menor),
    // pero NO entre 2+ adultos. Persons sin birthDate se tratan como adultas.
    const normalizedEmail = normalize.email(data.email || "");
    if (normalizedEmail && isAdultPerson(data)) {
      const adultsWithEmail =
        await personService.getAdultsByEmail(normalizedEmail);
      if (adultsWithEmail.length > 0) {
        const existing = adultsWithEmail[0];
        throw new Error(
          `El email ${normalizedEmail} ya está registrado para ${existing.fullName}. Use un email distinto o búsquela en la lista de personas.`,
        );
      }
    }

    const personData = {
      ...data,
      firstName: normalizedFirstName,
      lastName: normalizedLastName,
      fullName: data.fullName
        ? normalize.name(data.fullName)
        : normalize.fullName(data.firstName, data.lastName),
      email: normalize.email(data.email || ""),
      phone: normalize.phone(data.phone || ""),
      idDocumentType: normalizedDocType,
      idDocumentNumber: normalizedDocNumber,
      memberType: normalizeMemberType(data.memberType),
      gender: normalizeGender(data.gender),
      status: data.status || "active",
      // Normalizar contacto de emergencia
      emergencyContact: {
        name: normalize.name(data.emergencyContact?.name || ""),
        phone: normalize.phone(data.emergencyContact?.phone || ""),
        relationship: data.emergencyContact?.relationship || "",
      },
    };
    return create<Person>(COLLECTION, personData, userId);
  },

  async update(id: string, data: Partial<Person>): Promise<void> {
    const updateData = { ...data };

    // Normalizar campos si vienen en la actualización
    if (data.firstName !== undefined) {
      updateData.firstName = normalize.name(data.firstName);
    }
    if (data.lastName !== undefined) {
      updateData.lastName = normalize.name(data.lastName);
    }
    if (data.email !== undefined) {
      updateData.email = normalize.email(data.email);
    }
    if (data.phone !== undefined) {
      updateData.phone = normalize.phone(data.phone);
    }
    if (data.idDocumentType !== undefined) {
      updateData.idDocumentType = normalizeIdDocType(data.idDocumentType);
    }
    if (data.idDocumentNumber !== undefined) {
      updateData.idDocumentNumber = normalize.documentNumber(
        data.idDocumentNumber,
      );
    }

    // B3b: Fecha de nacimiento plausible si cambió (evita años mal digitados).
    if (
      data.birthDate !== undefined &&
      isImplausibleBirthDate(data.birthDate)
    ) {
      throw new Error(
        "La fecha de nacimiento no es válida (futura o edad fuera de rango). Revisa el año ingresado.",
      );
    }

    // B4: Validar unicidad del documento si cambió tipo o número
    if (
      data.idDocumentType !== undefined ||
      data.idDocumentNumber !== undefined
    ) {
      const current = await personService.getById(id);
      const finalType =
        updateData.idDocumentType ?? current?.idDocumentType ?? "CC";
      const finalNumber =
        updateData.idDocumentNumber ?? current?.idDocumentNumber ?? "";
      const typeChanged = current && finalType !== current.idDocumentType;
      const numberChanged = current && finalNumber !== current.idDocumentNumber;
      if (finalNumber && (typeChanged || numberChanged)) {
        const duplicate = await personService.getByDocument(
          finalType,
          finalNumber,
        );
        if (duplicate && duplicate.id !== id) {
          throw new Error(
            `Ya existe otra persona registrada con ${finalType} ${finalNumber} (${duplicate.fullName}).`,
          );
        }
        // B4b: NUIP — TI/CC comparten número por persona en Colombia.
        if (finalType === "CC" || finalType === "TI") {
          const sameNumber =
            await personService.getAllByDocumentNumber(finalNumber);
          const conflict = sameNumber.find(
            (p) =>
              p.id !== id &&
              (p.idDocumentType === "CC" || p.idDocumentType === "TI"),
          );
          if (conflict) {
            throw new Error(
              `El número ${finalNumber} ya pertenece a ${conflict.fullName} (${conflict.idDocumentType}). En Colombia la TI y la CC comparten el mismo número por persona; verifica el documento.`,
            );
          }
        }
      }
    }

    // B5: Validar unicidad de email entre adultos si cambió email o birthDate
    // (cambio de birthDate puede convertir un menor en adulto y crear conflicto).
    if (data.email !== undefined || data.birthDate !== undefined) {
      const current = await personService.getById(id);
      const finalEmail =
        updateData.email ?? normalize.email(current?.email || "");
      const finalBirth =
        data.birthDate !== undefined ? data.birthDate : current?.birthDate;
      const emailChanged =
        current && finalEmail !== normalize.email(current.email || "");
      const becameAdult =
        current &&
        !isAdultPerson({ birthDate: current.birthDate }) &&
        isAdultPerson({ birthDate: finalBirth });
      if (
        finalEmail &&
        (emailChanged || becameAdult) &&
        isAdultPerson({ birthDate: finalBirth })
      ) {
        const adultsWithEmail = await personService.getAdultsByEmail(
          finalEmail,
          id,
        );
        if (adultsWithEmail.length > 0) {
          const existing = adultsWithEmail[0];
          throw new Error(
            `El email ${finalEmail} ya está registrado para otra persona adulta (${existing.fullName}). Use un email distinto.`,
          );
        }
      }
    }
    if (data.memberType !== undefined) {
      updateData.memberType = normalizeMemberType(data.memberType);
    }
    if (data.gender !== undefined) {
      updateData.gender = normalizeGender(data.gender);
    }
    if (data.emergencyContact) {
      updateData.emergencyContact = {
        name: normalize.name(data.emergencyContact.name || ""),
        phone: normalize.phone(data.emergencyContact.phone || ""),
        relationship: data.emergencyContact.relationship || "",
      };
    }

    // Si se actualiza firstName o lastName, actualizar fullName
    if (data.firstName || data.lastName) {
      const current = await personService.getById(id);
      if (current) {
        const firstName = updateData.firstName || current.firstName;
        const lastName = updateData.lastName || current.lastName;
        updateData.fullName = normalize.fullName(firstName, lastName);
      }
    } else if (data.fullName !== undefined) {
      updateData.fullName = normalize.name(data.fullName);
    }

    await update<Person>(COLLECTION, id, updateData);

    // D2: Propagar fullName/photoUrl a todos los members de esta persona
    if (updateData.fullName || data.photoUrl !== undefined) {
      const { memberService } = await import("./members");
      const person = await personService.getById(id);
      if (person) {
        await memberService.syncPersonData(
          id,
          person.fullName,
          person.photoUrl,
        );
      }
    }
  },

  async delete(id: string): Promise<void> {
    return remove(COLLECTION, id);
  },

  /**
   * Desactivar persona (soft delete)
   */
  async deactivate(id: string): Promise<void> {
    return update<Person>(COLLECTION, id, { status: "inactive" });
  },

  /**
   * Reactivar persona
   */
  async activate(id: string): Promise<void> {
    return update<Person>(COLLECTION, id, { status: "active" });
  },

  // ==================== VINCULACIÓN ====================

  /**
   * Vincular persona con cuenta de usuario.
   *
   * BLINDADO POST-FASE-7:
   *   - Rechaza vinculación si el person ya tiene `linkedUserId` distinto.
   *   - Rechaza vinculación si el person ya tiene `guardianUserId` (debería
   *     resolverse por el trigger del servidor con privilegios admin, no
   *     desde el cliente que podría romper la relación de acudiente).
   *   - Si se pasa `expectedFullName`, valida que la similitud con el nombre
   *     de la persona sea ≥ 0.5 (Jaccard). Si no, rechaza.
   *   - Está marcada con un código de error `LINK_REJECTED_*` para que
   *     callers sepan que NO es un fallo de red.
   *
   * @param userPhotoUrl - Opcional: foto del usuario para sincronizar
   * @param expectedFullName - Opcional: nombre que el user reportó al
   *   registrarse. Si se provee, se valida similitud antes de vincular.
   */
  async linkToUser(
    personId: string,
    userId: string,
    userPhotoUrl?: string,
    expectedFullName?: string,
  ): Promise<void> {
    const person = await personService.getById(personId);
    if (!person) {
      throw new Error(`LINK_REJECTED_NOT_FOUND: person ${personId}`);
    }
    if (person.linkedUserId && person.linkedUserId !== userId) {
      throw new Error(
        `LINK_REJECTED_ALREADY_LINKED: person ${personId} ya vinculada a ${person.linkedUserId}`,
      );
    }
    if (person.guardianUserId && person.guardianUserId !== userId) {
      throw new Error(
        `LINK_REJECTED_HAS_GUARDIAN: person ${personId} ya tiene guardian ${person.guardianUserId}`,
      );
    }
    if (expectedFullName) {
      // Importar dinámicamente para evitar dependencia circular
      const { nameSimilarity } = await import("./members");
      const sim = nameSimilarity(person.fullName ?? "", expectedFullName);
      if (sim < 0.5) {
        throw new Error(
          `LINK_REJECTED_NAME_MISMATCH: similitud ${sim.toFixed(2)} entre "${person.fullName}" y "${expectedFullName}"`,
        );
      }
    }

    const updates: Partial<Person> = {
      linkedUserId: userId,
      linkedAt: Timestamp.now(),
    };

    // Si el usuario tiene foto y el Person no, sincronizar
    if (userPhotoUrl && !person.photoUrl) {
      updates.photoUrl = userPhotoUrl;
    }

    return update<Person>(COLLECTION, personId, updates);
  },

  /**
   * Sincronizar foto del usuario vinculado al Person y sus Members
   * Llamar cuando el usuario actualiza su foto de perfil
   */
  async syncUserPhoto(userId: string, photoUrl: string): Promise<void> {
    // Buscar Person vinculado
    const person = await personService.getByLinkedUser(userId);
    if (!person) return;

    // Actualizar Person si no tiene foto propia o si queremos sobrescribir
    // Solo actualizamos si el Person no tiene una foto subida manualmente
    if (!person.photoPath) {
      await update<Person>(COLLECTION, person.id, { photoUrl });

      // Importar memberService dinámicamente para evitar circular dependency
      const { memberService } = await import("./members");
      await memberService.syncPersonData(person.id, person.fullName, photoUrl);
    }
  },

  /**
   * Desvincular persona de cuenta de usuario
   */
  async unlinkFromUser(personId: string): Promise<void> {
    return update<Person>(COLLECTION, personId, {
      linkedUserId: undefined,
      linkedAt: undefined,
    } as Partial<Person>);
  },

  // ==================== UTILIDADES ====================

  /**
   * Verificar si ya existe una persona con el mismo documento
   */
  async exists(
    idDocumentType: Person["idDocumentType"],
    idDocumentNumber: string,
  ): Promise<boolean> {
    const person = await personService.getByDocument(
      idDocumentType,
      idDocumentNumber,
    );
    return person !== null;
  },

  /**
   * Buscar personas por texto (nombre o documento)
   * NOTA: Firestore no soporta búsqueda de texto nativa,
   * esto hace búsqueda client-side
   */
  async search(query: string, associationId?: string): Promise<Person[]> {
    const normalizedQuery = query.toLowerCase().trim();

    let persons: Person[];
    if (associationId) {
      persons = await personService.getByAssociation(associationId);
    } else {
      persons = await personService.getActive();
    }

    return persons.filter(
      (p) =>
        p.fullName.toLowerCase().includes(normalizedQuery) ||
        p.idDocumentNumber.includes(normalizedQuery) ||
        p.email?.toLowerCase().includes(normalizedQuery),
    );
  },

  // ==================== DOCUMENTOS ====================

  /**
   * Actualizar foto del anverso del documento de identidad
   */
  async updateDocumentFront(
    personId: string,
    data: {
      url: string;
      path: string;
      contentType: string;
      encryption?: { key: string; alg: string };
    },
  ): Promise<void> {
    await updateDoc(doc(db, COLLECTION, personId), {
      documentFrontUrl: data.url,
      documentFrontPath: data.path,
      documentFrontContentType: data.contentType,
      documentFrontEncryption: data.encryption || null,
      updatedAt: serverTimestamp(),
    });
  },

  /**
   * Actualizar foto del reverso del documento de identidad
   */
  async updateDocumentBack(
    personId: string,
    data: {
      url: string;
      path: string;
      contentType: string;
      encryption?: { key: string; alg: string };
    },
  ): Promise<void> {
    await updateDoc(doc(db, COLLECTION, personId), {
      documentBackUrl: data.url,
      documentBackPath: data.path,
      documentBackContentType: data.contentType,
      documentBackEncryption: data.encryption || null,
      updatedAt: serverTimestamp(),
    });
  },

  /**
   * Actualizar certificado EPS
   */
  async updateEpsDocument(
    personId: string,
    data: {
      url: string;
      path: string;
      contentType: string;
      encryption?: { key: string; alg: string };
    },
  ): Promise<void> {
    await updateDoc(doc(db, COLLECTION, personId), {
      epsDocumentUrl: data.url,
      epsDocumentPath: data.path,
      epsDocumentContentType: data.contentType,
      epsDocumentEncryption: data.encryption || null,
      updatedAt: serverTimestamp(),
    });
  },

  /**
   * Actualizar foto del participante
   */
  async updatePhoto(
    personId: string,
    data: {
      url: string;
      path: string;
    },
  ): Promise<void> {
    await updateDoc(doc(db, COLLECTION, personId), {
      photoUrl: data.url,
      photoPath: data.path,
      updatedAt: serverTimestamp(),
    });
  },

  /**
   * Persiste información médica declarativa + lugar de expedición CC + EPS
   * en el Person, para no volver a preguntarlos en futuros eventos.
   * Solo escribe campos definidos (los `undefined` se omiten).
   */
  async updateMedicalInfo(
    personId: string,
    data: {
      healthInsurance?: string;
      medicalConditions?: string;
      allergies?: string;
      medications?: string;
      noMedicalConditions?: boolean;
      idExpeditionCity?: string;
    },
  ): Promise<void> {
    const payload: Record<string, unknown> = { updatedAt: serverTimestamp() };
    for (const k of [
      "healthInsurance",
      "medicalConditions",
      "allergies",
      "medications",
      "noMedicalConditions",
      "idExpeditionCity",
    ] as const) {
      if (data[k] !== undefined) payload[k] = data[k];
    }
    await updateDoc(doc(db, COLLECTION, personId), payload);
  },
};

// ==================== FUNCIONES DE AYUDA ====================

/**
 * Asegura que existan Person y Member para un usuario.
 * Si no existen, los crea automáticamente.
 *
 * Usado cuando se asigna un rol como club_director.
 */
export async function ensurePersonAndMemberExist(params: {
  userId: string;
  email: string;
  fullName: string;
  clubId: string;
  role:
    | "director"
    | "subdirector"
    | "consejero"
    | "instructor"
    | "tesorero"
    | "secretario"
    | "member";
  createdBy: string;
  // Datos personales opcionales (del formulario de solicitud)
  firstName?: string;
  lastName?: string;
  phone?: string;
  birthDate?: Date;
  idDocumentType?: string;
  idDocumentNumber?: string;
  gender?: string;
  bloodType?: string;
  address?: string;
  healthInsurance?: string;
  emergencyContact?: { name: string; phone: string; relationship: string };
  memberType?: string;
  /**
   * Si true, la persona creada es un DEPENDIENTE (ward) gestionado por `userId`
   * (acudiente/padre), no la cuenta misma: se setea `guardianUserId = userId`
   * en vez de `linkedUserId`, y NO se toca `users.personId`. Usado cuando un
   * padre registra a su hijo en el club desde su propia cuenta.
   */
  asWard?: boolean;
  /** Nombre del acudiente (para denormalizar en el ward). Solo si `asWard`. */
  guardianName?: string;
}): Promise<{ personId: string; memberId: string }> {
  const {
    userId,
    email,
    fullName,
    clubId,
    role,
    createdBy,
    asWard,
    guardianName: providedGuardianName,
    firstName: providedFirstName,
    lastName: providedLastName,
    phone: providedPhone,
    birthDate: providedBirthDate,
    idDocumentType: providedIdDocType,
    idDocumentNumber: providedIdDocNumber,
    gender: providedGender,
    bloodType: providedBloodType,
    address: providedAddress,
    healthInsurance: providedHealthInsurance,
    emergencyContact: providedEmergencyContact,
    memberType: providedMemberType,
  } = params;

  // 1. Buscar Person existente por linkedUserId.
  // Si es un WARD (el padre registra a su hijo), NO buscamos por el userId del
  // padre — eso encontraría al padre, no al hijo. Para wards solo identificamos
  // por documento (paso 2b).
  let person = asWard ? null : await personService.getByLinkedUser(userId);

  // 1b. Detectar "suplantación accidental":
  // si el solicitante envió la solicitud desde una sesión que ya pertenece a OTRA
  // persona registrada (ej: una mamá usando el navegador del director), los datos
  // del formulario (firstName/lastName/idDocumentNumber) NO van a coincidir con
  // la Person dueña de ese userId. En ese caso NO debemos reutilizar esa Person:
  // hay que crear una nueva (sin linkedUserId, ya está ocupado).
  if (person) {
    const provDoc = (providedIdDocNumber || "").trim();
    const personDoc = (person.idDocumentNumber || "").trim();
    const provFirst = (providedFirstName || "").trim().toLowerCase();
    const personFirst = (person.firstName || "").trim().toLowerCase();
    const provLast = (providedLastName || "").trim().toLowerCase();
    const personLast = (person.lastName || "").trim().toLowerCase();

    const docMismatch = provDoc && personDoc && provDoc !== personDoc;
    const nameMismatch =
      (provFirst && personFirst && provFirst !== personFirst) ||
      (provLast && personLast && provLast !== personLast);

    if (docMismatch || nameMismatch) {
      console.warn(
        `[ensurePersonAndMemberExist] Datos del formulario (${providedFirstName} ${providedLastName} / ${provDoc}) no coinciden con Person ${person.id} vinculada al userId ${userId} (${person.fullName} / ${personDoc}). Se creará una Person separada para no sobrescribir.`,
      );
      person = null;
    }
  }

  // 2. Si no existe, buscar por email (NO para wards: el hijo suele compartir
  // el email del padre, y reusar por email lo confundiría con la cuenta).
  if (!person && !asWard) {
    const personsByEmail = await getDocs(
      query(
        collection(db, "persons"),
        where("email", "==", email.toLowerCase()),
        limit(1),
      ),
    );

    if (!personsByEmail.empty) {
      const personDoc = personsByEmail.docs[0];
      const candidate = { id: personDoc.id, ...personDoc.data() } as Person;

      // Mismo chequeo de discrepancia con candidato por email
      const provDoc = (providedIdDocNumber || "").trim();
      const candDoc = (candidate.idDocumentNumber || "").trim();
      const provFirst = (providedFirstName || "").trim().toLowerCase();
      const candFirst = (candidate.firstName || "").trim().toLowerCase();
      const docMismatch = provDoc && candDoc && provDoc !== candDoc;
      const nameMismatch = provFirst && candFirst && provFirst !== candFirst;

      if (!docMismatch && !nameMismatch) {
        person = candidate;
        // Vincular al userId si no está vinculado y nadie más usa este userId
        if (!person.linkedUserId) {
          await personService.linkToUser(person.id, userId);
          person.linkedUserId = userId;
        }
      }
    }
  }

  // 2b. Buscar por documento (si vino) — más confiable que email para identificar.
  // REUSO cross-type (TI/CC comparten número en Colombia → misma persona) e
  // incluye fichas inactivas/huérfanas. Así REUTILIZAMOS en vez de chocar contra
  // la guarda de unicidad al crear (que sí bloquea por número) y dejar al director
  // sin salida. Si la ficha hallada estaba inactiva, se reactiva.
  if (!person && providedIdDocNumber && providedIdDocType) {
    const existingByDoc = await personService.findReusableByDocument(
      providedIdDocType,
      providedIdDocNumber.trim(),
    );
    if (existingByDoc) {
      person = existingByDoc;
      if (existingByDoc.status && existingByDoc.status !== "active") {
        await updateDoc(doc(db, COLLECTION, existingByDoc.id), {
          status: "active",
          updatedAt: serverTimestamp(),
          updatedBy: createdBy,
        });
        person = { ...existingByDoc, status: "active" };
      }
    }
  }

  // 3. Si aún no existe, crear Person
  if (!person) {
    // Obtener info del club para churchId y associationId
    const clubDocRef = doc(db, "clubs", clubId);
    const clubSnapshot = await getDoc(clubDocRef);
    if (!clubSnapshot.exists()) {
      throw new Error(`Club no encontrado: ${clubId}`);
    }
    const clubData = clubSnapshot.data();

    // Usar datos proporcionados o parsear del nombre
    const normalizedFullName = normalize.name(fullName);
    const nameParts = normalizedFullName.trim().split(" ");
    const firstName = providedFirstName || nameParts[0] || "";
    const lastName = providedLastName || nameParts.slice(1).join(" ") || "";

    // Mapear tipo de sangre (normalizar formato)
    const mapBloodType = (type?: string): BloodType => {
      const validTypes: BloodType[] = [
        "A+",
        "A-",
        "B+",
        "B-",
        "AB+",
        "AB-",
        "O+",
        "O-",
      ];
      const normalized = type
        ?.replace(/positive/i, "+")
        .replace(/negative/i, "-");
      const mapped = validTypes.find(
        (bt) => bt.toLowerCase() === normalized?.toLowerCase(),
      );
      return mapped || "O+";
    };

    // Verificar si el userId ya está vinculado a otra Person (caso suplantación
    // accidental: la solicitud se envió desde la sesión de otra persona). En ese
    // caso NO debemos volver a usar ese linkedUserId aquí — la nueva Person
    // queda sin linkedUserId hasta que el verdadero dueño se registre.
    const existingByUser = asWard
      ? null
      : await personService.getByLinkedUser(userId);
    const safeLinkedUserId = existingByUser ? undefined : userId;
    // Para wards: vincular al ACUDIENTE (guardianUserId), no como cuenta propia.
    const linkFields: Record<string, unknown> = asWard
      ? { guardianUserId: userId, guardianName: providedGuardianName || "" }
      : safeLinkedUserId
        ? { linkedUserId: safeLinkedUserId }
        : {};

    const personId = await personService.create(
      {
        firstName,
        lastName,
        fullName: `${firstName} ${lastName}`.trim() || normalizedFullName,
        email: normalize.email(email),
        phone: providedPhone || "",
        idDocumentType: normalizeIdDocType(providedIdDocType || "CC"),
        idDocumentNumber: providedIdDocNumber || "",
        birthDate: providedBirthDate
          ? Timestamp.fromDate(providedBirthDate)
          : (() => {
              throw new Error(
                "birthDate es obligatorio para crear una persona",
              );
            })(),
        gender: normalizeGender(providedGender),
        bloodType: mapBloodType(providedBloodType),
        memberType: normalizeMemberType(providedMemberType),
        address: providedAddress || "",
        healthInsurance: providedHealthInsurance || "",
        churchId: clubData.churchId || "",
        associationId: clubData.associationId || "",
        emergencyContact: providedEmergencyContact || {
          name: "",
          phone: "",
          relationship: "",
        },
        ...linkFields,
        status: "active",
      } as Omit<Person, "id" | "createdAt" | "updatedAt">,
      createdBy,
    );

    person = await personService.getById(personId);
  }

  if (!person) {
    throw new Error("No se pudo crear o encontrar la persona");
  }

  // 3b. Ward reusado por documento sin vínculo: asegurar guardianUserId.
  if (asWard && !person.linkedUserId && !person.guardianUserId && person.id) {
    await updateDoc(doc(db, COLLECTION, person.id), {
      guardianUserId: userId,
      guardianName: providedGuardianName || "",
      updatedAt: serverTimestamp(),
      updatedBy: createdBy,
    });
  }

  // 4. Buscar Member existente para este Person en este Club
  const memberQuery = query(
    collection(db, "members"),
    where("personId", "==", person.id),
    where("clubId", "==", clubId),
    limit(1),
  );
  const existingMember = await getDocs(memberQuery);

  let memberId: string;

  if (!existingMember.empty) {
    // Actualizar rol si ya existe
    memberId = existingMember.docs[0].id;
    const memberData = existingMember.docs[0].data();

    if (memberData.role !== role) {
      const memberDocRef = doc(db, "members", memberId);
      await updateDoc(memberDocRef, {
        role,
        roleStartDate: serverTimestamp(),
        updatedAt: serverTimestamp(),
        updatedBy: createdBy,
      });
    }
  } else {
    // Crear nuevo Member usando memberService.create() para reusar el guard
    // anti-doble-submit (docId determinístico clubId__personId + transacción).
    // M3-fix: antes este path usaba addDoc() directo y permitía duplicados al
    // aprobar 2 veces una solicitud o por doble-tap.
    const clubDocRef = doc(db, "clubs", clubId);
    const clubSnapshot = await getDoc(clubDocRef);
    const clubData = clubSnapshot.data()!;

    const { memberService } = await import("./members");
    memberId = await memberService.create(
      {
        personId: person.id,
        clubId,
        churchId: clubData.churchId || "",
        associationId: clubData.associationId || "",
        section: (providedBirthDate
          ? deriveSectionFromBirthDate(providedBirthDate)
          : clubData.type || "conquistadores") as any,
        role: role as any,
        memberType: normalizeMemberType(providedMemberType) as any,
        roleStartDate: Timestamp.now(),
        status: "active",
        ...(role === "director" ? { position: "Director" } : {}),
        registrationDate: Timestamp.now(),
        registrationPaid: true,
        personName: person.fullName,
        personPhoto: person.photoUrl || "",
      } as any,
      createdBy,
    );
  }

  return { personId: person.id, memberId };
}

// ==================== REALTIME SUBSCRIPTIONS ====================

/** Suscripción en tiempo real a una persona por ID */
export function subscribeToPerson(
  id: string,
  callback: (person: Person | null) => void,
): () => void {
  return subscribeToDocument<Person>("persons", id, callback);
}

/** Suscripción en tiempo real a personas por asociación */
export function subscribeToPersonsByAssociation(
  associationId: string,
  callback: (persons: Person[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return subscribeToCollection<Person>(
    "persons",
    callback,
    [
      where("associationId", "==", associationId),
      where("status", "==", "active"),
      orderBy("fullName", "asc"),
    ],
    onError,
  );
}

/** Suscripción en tiempo real a todas las personas activas */
export function subscribeToActivePersons(
  callback: (persons: Person[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return subscribeToCollection<Person>(
    "persons",
    callback,
    [where("status", "==", "active"), orderBy("fullName", "asc")],
    onError,
  );
}

// ==================== GUARDIAN MODEL ====================

/**
 * Devuelve las personas a cargo (wards) de un usuario acudiente.
 * Es decir, todas las Person con `guardianUserId == userId` y status active.
 */
export async function getWardsOf(userId: string): Promise<Person[]> {
  if (!userId) return [];
  const snap = await getDocs(
    query(
      collection(db, COLLECTION),
      where("guardianUserId", "==", userId),
      where("status", "==", "active"),
    ),
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Person);
}

/**
 * Suscripción en tiempo real a los menores/personas a cargo del usuario.
 */
export function subscribeToWardsOf(
  userId: string,
  callback: (wards: Person[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return subscribeToCollection<Person>(
    "persons",
    callback,
    [
      where("guardianUserId", "==", userId),
      where("status", "==", "active"),
      orderBy("fullName", "asc"),
    ],
    onError,
  );
}

/**
 * Devuelve el acudiente (guardianUserId) de una persona, o null si no tiene.
 * Solo retorna el ID; el caller debe resolver el User si lo necesita.
 */
export async function getGuardianOf(personId: string): Promise<string | null> {
  if (!personId) return null;
  const snap = await getDoc(doc(db, COLLECTION, personId));
  if (!snap.exists()) return null;
  const data = snap.data() as Partial<Person>;
  return data.guardianUserId ?? null;
}

/**
 * Campos que un guardian puede actualizar en su ward.
 * Debe coincidir con la whitelist de firestore.rules → match /persons.
 */
export const WARD_EDITABLE_FIELDS = [
  "phone",
  "address",
  "healthInsurance",
  "medicalConditions",
  "allergies",
  "medications",
  "noMedicalConditions",
  "emergencyContact",
  "photoUrl",
  "photoPath",
  "guardianRelation",
  "documentFrontUrl",
  "documentFrontPath",
  "documentFrontEncryption",
  "documentFrontContentType",
  "documentBackUrl",
  "documentBackPath",
  "documentBackEncryption",
  "documentBackContentType",
  "epsDocumentUrl",
  "epsDocumentPath",
  "epsDocumentEncryption",
  "epsDocumentContentType",
] as const;

export type WardEditableField = (typeof WARD_EDITABLE_FIELDS)[number];

/**
 * Actualiza únicamente campos no críticos de un menor a cargo (whitelist).
 * Las firestore.rules rechazarán cualquier campo fuera de la whitelist.
 */
export async function updateWardSafe(
  personId: string,
  patch: Partial<Pick<Person, WardEditableField>>,
  updatedBy: string,
): Promise<void> {
  const clean: Record<string, unknown> = {};
  for (const k of WARD_EDITABLE_FIELDS) {
    if (k in patch) {
      const v = (patch as Record<string, unknown>)[k];
      if (v !== undefined) clean[k] = v;
    }
  }
  clean.updatedAt = serverTimestamp();
  clean.updatedBy = updatedBy;
  await updateDoc(doc(db, COLLECTION, personId), clean);
}

/**
 * Sólo para pruebas: calcAge e isAdultPerson deciden si un correo se puede
 * compartir (B5), y un fallo ahí bloquea a familias enteras en silencio.
 */
export const __testing = { calcAge, isAdultPerson };
